import React from 'react';
import { Box, Typography } from '@mui/material';
import { useHolidays } from '../contexts/HolidayContext';
import { MergedHoliday } from '../types';

export const NextHolidayCountdown: React.FC = () => {
  const { allHolidays } = useHolidays();
  const [countdown, setCountdown] = React.useState<{
    mainText: string;
    extraText: string;
  } | null>(null);

  React.useEffect(() => {
    if (allHolidays.length === 0) {
      setCountdown(null);
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Convert dateString to Date and find next holiday
    const holidaysWithDates = allHolidays.map((holiday) => {
      const year = parseInt(holiday.dateString.substring(0, 4));
      const month = parseInt(holiday.dateString.substring(4, 6)) - 1;
      const day = parseInt(holiday.dateString.substring(6, 8));
      return {
        ...holiday,
        date: new Date(year, month, day),
      };
    });

    const futureHolidays = holidaysWithDates
      .filter((holiday) => holiday.date >= today)
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    if (futureHolidays.length === 0) {
      setCountdown(null);
      return;
    }

    const nextHoliday = futureHolidays[0];
    const daysUntil = Math.ceil(
      (nextHoliday.date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
    );

    let extraText = "";
    // if it is part-day, show "but only for part of the day!"
    if (nextHoliday.partDay) {
      extraText = " (but only for part of the day!)";
    }
    // if it's regionalonly, show "lucky people in [regionalInfo]!"
    else if (nextHoliday.regionalOnly && nextHoliday.regionalInfo) {
      extraText = ` (lucky people in ${nextHoliday.regionalInfo}!)`;
    }
    // if it's special conditions show "conditions apply*"
    else if (nextHoliday.specialConditions) {
      extraText = " (conditions apply*)";
    }

    let mainText = "";
    if (daysUntil === 0) {
      mainText = `Today is ${nextHoliday.holidayName}! 🎉`;
    } else if (daysUntil === 1) {
      mainText = `1 day till ${nextHoliday.holidayName}!`;
    } else {
      mainText = `${daysUntil} days till ${nextHoliday.holidayName}!`;
    }

    setCountdown({ mainText, extraText });
  }, [allHolidays]);

  if (!countdown) {
    return null;
  }

  return (
    <Box sx={{ textAlign: "center", mb: 1.5, mt: -1 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
        <span style={{ fontWeight: 600 }}>{countdown.mainText}</span>
        {countdown.extraText && (
          <span style={{ fontStyle: 'italic', fontWeight: 400 }}>{countdown.extraText}</span>
        )}
      </Typography>
    </Box>
  );
};
